import React, { useCallback, useState } from 'react';

import { Form } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import Popover from 'react-bootstrap/Popover';
import { Search, X } from 'react-bootstrap-icons';

import classNames from 'classnames';
import { Filter } from '../../../Enum/Filters';
import { filterChanged } from '../../../Store/Filter/action';
import { Label } from '../../../Interface/Response/Interest/Label';

import { useDispatch } from 'react-redux';
import useGetFilteredResults from '../../../Hook/UseGetFilteredResults';
import useGetFilterValue from '../../../Hook/Interest/UseGetFilterValue';
import useGetLabels from '../../../Hook/Interest/UseGetLabels';

interface Props {
    filterType?: Filter;
    label?: string;
    placeholder: string;
    maxAmountSelectedLabels: number;
    showExistingCustomers: boolean;
    setFilter?: boolean;
}

interface State {
    showPopover: boolean;
    keyword: string;
    selected: Label[];
}

const filterLabels = (finDataCodes: string[], allLabels: Label[]): Label[] => finDataCodes.map(
    (finDataCode: string) => allLabels.find(iteratedLabel => finDataCode === iteratedLabel.findataCode) as Label
);

const LabelMultiSelectFilter: React.FC<Props> = ({
    filterType,
    label,
    placeholder,
    maxAmountSelectedLabels,
    showExistingCustomers,
    setFilter = false,
}: Props) => {
    const dispatch = useDispatch();
    const { allLabels } = useGetLabels(showExistingCustomers, setFilter);
    const selectedLabels = useGetFilterValue(filterType || Filter.Labels) as string[];
    const [state, setState] = useState<State>({
        showPopover: false,
        keyword: '',
        selected: filterLabels(selectedLabels, allLabels)
    });
    const filteredLabels = useGetFilteredResults(allLabels, state.keyword, 0);

    const toggleLabel = useCallback(
        (toggledLabel: Label): void => {
            const dispatchFilter = (labels: string[]): void => {
                setState(prev => ({ ...prev, selected: filterLabels(labels, allLabels) }));
                dispatch(filterChanged(filterType || Filter.Labels, labels));
            };

            const inSelected = selectedLabels.find(code => code === toggledLabel.findataCode);
            if (!inSelected) {
                dispatchFilter([...selectedLabels, toggledLabel.findataCode]);
            }

            if (inSelected) {
                dispatchFilter(selectedLabels.filter(code => code !== toggledLabel.findataCode));
            }

            if (selectedLabels.length === maxAmountSelectedLabels) {
                setState(prev => ({ ...prev, showPopover: false }));
            }
        },
        [dispatch, filterType, selectedLabels, state.showPopover, state.keyword],
    );

    return (
        <>
            <Form className="popover-select-form" autoComplete="off">
                <Form.Group controlId="label-select">
                    {label && <Form.Label>{label}</Form.Label>}
                    <InputGroup>
                        <InputGroup.Prepend
                            className={classNames({ disabled: selectedLabels.length === maxAmountSelectedLabels })}
                        >
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                        </InputGroup.Prepend>
                        <Form.Control
                            type="text"
                            className="rounded-right"
                            placeholder={placeholder}
                            disabled={selectedLabels.length === maxAmountSelectedLabels}
                            onChange={(e: any) => setState({ ...state, keyword: e.target.value })}
                            onFocus={() => setState({ ...state, showPopover: true })}
                        />
                        {filteredLabels.length > 0 &&
                            state.showPopover &&
                            selectedLabels.length < maxAmountSelectedLabels && (
                                <>
                                    <div
                                        className="popover-backdrop"
                                        onClick={() => setState({ ...state, showPopover: false })}
                                        onKeyDown={() => setState({ ...state, showPopover: false })}
                                    />
                                    <Popover id="labels-select-popover" className="select" content placement="bottom">
                                        {filteredLabels.map(filteredLabel => (
                                            <p
                                                key={`available-${filteredLabel.findataCode}`}
                                                onClick={() => toggleLabel(filteredLabel)}
                                                onKeyDown={() => toggleLabel(filteredLabel)}
                                                className={classNames(
                                                    'd-flex align-items-center',
                                                    {
                                                        active: selectedLabels.find(
                                                            findataCode => findataCode === filteredLabel.findataCode,
                                                        ),
                                                    }
                                                )}
                                            >
                                                <img
                                                    className="icon-sm mr-1"
                                                    src={filteredLabel.iconUrl}
                                                    alt={filteredLabel.label}
                                                />
                                                {filteredLabel.label}
                                                {selectedLabels.find(
                                                    findataCode => findataCode === filteredLabel.findataCode,
                                                ) && <X />}
                                            </p>
                                        ))}
                                    </Popover>
                                </>
                            )}
                    </InputGroup>
                    {state.selected.length > 0 && (
                        <>
                            <hr className="mb-2 mt-2" />
                            {state.selected.map(label => (
                                <div className="selected-value-wrapper labels" key={`selected-${label.findataCode}`}>
                                    <div
                                        className="selected-value-wrapper__value"
                                        onClick={() => toggleLabel(label)}
                                        onKeyDown={() => toggleLabel(label)}
                                    >
                                        <img className="icon-sm mr-1" src={label.iconUrl} alt={label.label} />
                                        {label.label}
                                        <X />
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </Form.Group>
            </Form>
        </>
    );
};

export default LabelMultiSelectFilter;
